import { useController, useFormContext } from "react-hook-form";
import { Search } from "lucide-react";
import { FormField } from "@shared/ui/FormField";
import { Select, OptionType } from "@shared/ui/Select";

type Props = {
  name: string;
  options: OptionType[];
  isLoading: boolean;
  value?: OptionType | null;
  onSelect?: (val: OptionType | null) => void;
  onCreate?: (option: OptionType) => void;
  className?: string;
};

export function OrgLookupInput({
  name,
  options,
  isLoading,
  value,
  onSelect,
  onCreate,
  className,
}: Props) {
  const { control } = useFormContext();

  const {
    field: { value: rhfValue, onChange, onBlur },
    fieldState: { error },
    formState,
  } = useController({
    name,
    control,
    defaultValue: null,
  });

  const useControlled =
    typeof value !== "undefined" && typeof onSelect === "function";
  const effectiveValue = useControlled ? value : rhfValue;

  const handleChange = (selected: OptionType | null) => {
    if (useControlled) {
      onSelect?.(selected);
    } else {
      onChange(selected);
    }
  };

  return (
    <FormField
      label="Select or enter any program you are affiliated with"
      id={name}
      icon={<Search />}
      error={error}
      isLoading={formState.isLoading}
      isSubmitting={formState.isSubmitting}
      className={className}
    >
      <Select
        name={name}
        isCreatable
        options={options}
        isLoading={isLoading}
        isWithIcon
        placeholder="School, residency, or fellowship."
        noOptionsMessage="Search for an organization or press enter to add a new one."
        inputId={name}
        value={
          options.find(
            (o) =>
              o.value === effectiveValue?.value || o.value === effectiveValue
          ) ??
          (typeof effectiveValue === "string" && effectiveValue
            ? { label: effectiveValue, value: effectiveValue }
            : effectiveValue || null)
        }
        onSelect={handleChange}
        onCreate={onCreate}
      />
    </FormField>
  );
}

export default OrgLookupInput;
